/**
 * Contract shared by the two dashboard map layers.
 *
 * SW2-2247. Google restricted the GCP account holding the production Maps key, so the
 * dashboard map needs a Mapbox path. This is a **reversible fallback, not a
 * migration**: both layers stay in the tree and switching back is
 * REACT_APP_MAP_PROVIDER=google plus a rebuild, never a code revert.
 *
 * Everything provider-agnostic — filters, data fetching, the legend, the project
 * modal — stays in Maps.tsx. Only the map surface itself is swapped, and both
 * implementations satisfy this one props type so Maps.tsx cannot tell them apart.
 */

/** One marker colour per distinct value of the active filter key. */
export const DEFAULT_MARKER_COLOR = '#DB4437';

export type DashboardMapProps = {
  /** Project rows from the dashboard query. Untyped upstream; kept as-is. */
  mapData: any[];
  /** Filter value -> hex colour, assigned by Maps.tsx. */
  filterWiseHexColor: Record<string, any>;
  /** Which field of a row the colouring is keyed on. */
  currentKey: string;
  /**
   * Where the map should be centred. Reassigned by the parent when a marker is
   * clicked, so both layers must recentre when this changes — that is how the Google
   * layer has always behaved, since it passes this straight to `<GoogleMap center>`.
   */
  center: { lat: number; lng: number };
  /**
   * A marker was clicked. The parent owns the selection, the recentre and the modal;
   * neither layer knows those exist.
   */
  onSelectProject: (project: any) => void;
};

/**
 * Guards against the junk coordinates the projects table carries — `addresses.latitude`
 * and `longitude` are varchar, and a large number of rows hold "", "." or placeholders.
 * Moved here unchanged so Maps.tsx and both layers apply exactly the same test.
 */
export const isProperCoordinates = (lat: number, lng: number) => {
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    !isNaN(lat) &&
    !isNaN(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
};

/** The tooltip Google put on a pin: project name, then the inspector on a second line. */
export const markerTooltip = (item: any): string => {
  const parts = [item.projectName];

  if (item.inspectorName) parts.push(`Inspector: ${item.inspectorName}`);

  return parts.join('\n');
};
